/**
 * useOtpAuthentication.js
 * Business logic extracted from OtpPanel.js.
 *
 * DRY fix: the original component's `validate()` function reimplemented
 * the email/phone regex checks that already exist in validators.js.
 * This hook now calls `validators.email` / `validators.phone` directly —
 * there's exactly one place each rule lives.
 */

import { useState } from "react";
import { useAsync } from "../../../shared/hooks/useAsync";
import { useCountdown } from "../../../shared/hooks/useCountdown";
import {
    DEFAULT_PHONE_COUNTRY_CODE,
    OTP_RESEND_COOLDOWN_SECONDS,
    OTP_TABS,
} from "../constants/authentication.constants";
import { authenticationApi } from "../api/authentication.api";
import { validators } from "../validation/authentication.validation";
import { useSession } from "./useSession";

const STEP = { INPUT: "INPUT", OTP: "OTP" };

export function useOtpAuthentication({ onSuccess }) {
    const [step, setStep] = useState(STEP.INPUT);
    const [tab, setTab] = useState(OTP_TABS.EMAIL);
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [fieldError, setFieldError] = useState("");

    const { run, loading, error } = useAsync();
    const { seconds, start, canResend } = useCountdown(OTP_RESEND_COOLDOWN_SECONDS);
    const { establishSession } = useSession();

    const identifier = tab === OTP_TABS.EMAIL ? email : `${DEFAULT_PHONE_COUNTRY_CODE}${phone}`;

    const validateIdentifier = () => {
        const message = tab === OTP_TABS.EMAIL ? validators.email(email) : validators.phone(phone);
        setFieldError(message ?? "");
        return message === null;
    };

    const sendOtp = async () => {
        if (!validateIdentifier()) return;
        const data = await run(() => authenticationApi.sendOtpToIdentifier(identifier));
        if (data === null) return;
        setStep(STEP.OTP);
        start();
    };

    const verifyOtp = async () => {
        const otpError = validators.otp(otp);
        if (otpError) {
            setFieldError(otpError);
            return;
        }
        setFieldError("");

        const data = await run(() => authenticationApi.verifyOtpForIdentifier(identifier, otp));
        if (!data?.token) return;

        establishSession(data.token);
        onSuccess?.();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        step === STEP.INPUT ? sendOtp() : verifyOtp();
    };

    const switchTab = (nextTab) => {
        setTab(nextTab);
        setFieldError("");
        setStep(STEP.INPUT);
        setOtp("");
    };

    return {
        STEP,
        step,
        tab,
        email,
        phone,
        otp,
        identifier,
        fieldError,
        loading,
        error,
        seconds,
        canResend,
        setEmail,
        setPhone,
        setOtp,
        switchTab,
        sendOtp,
        handleSubmit,
    };
}
